import {
  ApiPeopleAnalyticsAggregation,
  ApiPeopleAnalyticsColumn,
} from "@/types/reporting/people-analytics.types.ts";
import {
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSub,
  FontAwesomeIcon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

type SortDirection = "asc" | "desc";

interface ColumnAggregationHeaderProps {
  column: ApiPeopleAnalyticsColumn;
  aggregation: ApiPeopleAnalyticsAggregation;
  warningHint?: string;
  onChange: (aggregation: ApiPeopleAnalyticsAggregation) => void;
  sortDirection?: SortDirection | null;
  onSort?: (direction: SortDirection | null) => void;
}

export function ColumnAggregationHeader({
  column,
  aggregation,
  warningHint,
  onChange,
  sortDirection,
  onSort,
}: ColumnAggregationHeaderProps) {
  const { t } = useTranslation();

  const aggregationTitle = (value: ApiPeopleAnalyticsAggregation) =>
    t(`reporting:people_analytics_aggregation_${value}`);

  const hintIcon = warningHint ? (
    <Tooltip delay={false}>
      <TooltipTrigger>
        <FontAwesomeIcon icon="fa-light fa-circle-info" className="text-secondary" />
      </TooltipTrigger>
      <TooltipContent className="max-w-60 whitespace-pre-line">{warningHint}</TooltipContent>
    </Tooltip>
  ) : null;

  if (column.aggregations.length <= 1) {
    return (
      <Stack direction="horizontal" gap="xs" className="items-center">
        <Text>{column.title}</Text>
        {hintIcon}
      </Stack>
    );
  }

  return (
    <DropdownMenu
      trigger={
        <Stack
          direction="horizontal"
          gap="xs"
          className="group/header w-full items-center cursor-pointer"
        >
          <Text>{column.title}</Text>
          {hintIcon}
          <ButtonIcon
            variant="plain"
            icon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
            className="opacity-0 transition-opacity group-hover/header:opacity-100"
          ></ButtonIcon>
        </Stack>
      }
    >
      {onSort && (
        <DropdownMenuSub title={t("reporting:people_analytics_sort")}>
          <DropdownMenuItem
            title={t("reporting:people_analytics_sort_asc")}
            isSelected={sortDirection === "asc"}
            onClick={() => onSort(sortDirection === "asc" ? null : "asc")}
          />
          <DropdownMenuItem
            title={t("reporting:people_analytics_sort_desc")}
            isSelected={sortDirection === "desc"}
            onClick={() => onSort(sortDirection === "desc" ? null : "desc")}
          />
          <DropdownMenuItem
            title={t("reporting:people_analytics_sort_none")}
            isSelected={!sortDirection}
            onClick={() => onSort(null)}
          />
        </DropdownMenuSub>
      )}
      {column.aggregations.map((value) => (
        <DropdownMenuItem
          key={value}
          title={aggregationTitle(value)}
          isSelected={value === aggregation}
          onClick={() => onChange(value)}
        />
      ))}
    </DropdownMenu>
  );
}
